import React, { useState, useEffect, useRef } from 'react';
import { ServiceStep, AreaStep, BudgetStep, SearchButton } from './SearchFormSteps';
import ToggleSwitch from '../ui/ToggleSwitch';
import FuzzySearchForm from './FuzzySearchForm';
import '../../styles/components/ui/ToggleSwitch.scss';
import { useFilters } from '../../hooks/useFilters';
import { useSearchState } from '../../contexts/SearchStateContext';
import { FaSearch, FaRobot, FaExclamationTriangle, FaLock, FaTimes, FaUser } from 'react-icons/fa';
import { checkSRASearchStatus, getUserAiCredits } from '../../services/sraSearchService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';

const SearchForm = ({
  serviceOptions,
  areaOptions,
  hasUserInput,
  setHasUserInput,
  handleGlobalSearch,
  handleFuzzySearch,
  handleAISearch,
  animationStarted,
  contentAnimating,
  shiftLeftActive,
  isMobileView,
  initialService = null,
  initialArea = null,
  initialBudget = null,
  fuzzySearchEnabled = false,
  toggleFuzzySearch,
  fuzzySearchCriteria = {}
}) => {
  const { searchState, saveFuzzySearchCriteria, saveAiSearchState } = useSearchState();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [dropdownExpanded, setDropdownExpanded] = useState(false);
  
  // Search mode state - 'standard', 'quick', or 'ai' - initialize from preserved state
  const [searchMode, setSearchMode] = useState(
    searchState?.searchType || 'standard'
  );
  
  // AI search state - initialize from preserved state if available
  const [aiQuery, setAiQuery] = useState(
    searchState?.aiSearchCriteria?.query || ''
  );
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isAiAvailable, setIsAiAvailable] = useState(true);
  const [aiError, setAiError] = useState(null);
  const [aiPlaceholder, setAiPlaceholder] = useState('Ask about solicitors in natural language...');
  const [userCredits, setUserCredits] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Determine initial search mode based on fuzzySearchEnabled
  const [isFuzzySearchEnabled, setIsFuzzySearchEnabled] = useState(
    searchState.fuzzySearchEnabled || fuzzySearchEnabled
  );
  
  // Get practice areas for fuzzy search form
  const { practiceAreas } = useFilters();
  
  // Check AI search availability and user credits on component mount
  useEffect(() => {
    const checkAiStatus = async () => {
      try {
        const isAvailable = await checkSRASearchStatus();
        setIsAiAvailable(isAvailable);
        
        if (!isAvailable) {
          setAiPlaceholder('AI search temporarily unavailable');
          setAiError('AI search temporarily unavailable');
        }
      } catch (error) {
        console.error('Error checking AI search status:', error);
        setIsAiAvailable(false);
        setAiPlaceholder('AI search temporarily unavailable');
        setAiError('AI search temporarily unavailable');
      }
    };
    
    checkAiStatus();
  }, []);

  // Fetch user credits when user is authenticated
  useEffect(() => {
    const fetchUserCredits = async () => {
      if (user) {
        try {
          const creditsData = await getUserAiCredits();
          setUserCredits(creditsData);
        } catch (error) {
          console.error('Error fetching user credits:', error);
        }
      } else {
        setUserCredits(null);
      }
    };
    
    fetchUserCredits();
  }, [user]);

  // Listen for credit updates from AI search
  useEffect(() => {
    const handleCreditsUpdate = (event) => {
      console.log('SearchForm received credit update:', event.detail);
      setUserCredits(event.detail);
    };

    window.addEventListener('aiCreditsUpdated', handleCreditsUpdate);
    return () => window.removeEventListener('aiCreditsUpdated', handleCreditsUpdate);
  }, []);

  // Example queries for AI search placeholder
  useEffect(() => {
    if (!isAiAvailable || aiError) return;
    
    const examples = [
      'Find me divorce solicitors in London with 4+ star reviews',
      'Who are the best property solicitors in Manchester?',
      'Show solicitors specialising in immigration with good ratings',
      'Find me employment solicitors who can help with discrimination cases',
      'Which solicitors near Birmingham handle medical negligence?'
    ];
    
    let currentIndex = 0;
    const interval = setInterval(() => {
      setAiPlaceholder(examples[currentIndex]);
      currentIndex = (currentIndex + 1) % examples.length;
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isAiAvailable, aiError]);

  // Update search mode when fuzzy search is enabled from props (only if not AI mode)
  useEffect(() => {
    if (searchMode !== 'ai') {
      if (isFuzzySearchEnabled) {
        setSearchMode('quick');
      } else {
        setSearchMode('standard');
      }
    }
  }, [isFuzzySearchEnabled, searchMode]);

  // Reset AI mode if user logs out
  useEffect(() => {
    if (!user && searchMode === 'ai') {
      setSearchMode('standard');
    }
  }, [user, searchMode]);
  
  // Make sure initial values have proper structure before using them
  const normalizeOption = (option) => {
    if (!option) return null;
    
    if (typeof option === 'object' && option !== null) {
      return {
        label: option.label || option.value || String(option),
        value: option.value || option.label || String(option)
      };
    }
    
    if (typeof option === 'string') {
      return {
        label: option,
        value: option
      };
    }
    
    return null;
  };
  
  // Normalize initial values
  const normalizedInitialService = normalizeOption(initialService);
  const normalizedInitialArea = normalizeOption(initialArea);
  const normalizedInitialBudget = normalizeOption(initialBudget);
  
  console.log('Normalized initial service:', normalizedInitialService);
  
  const [selectedService, setSelectedService] = useState(normalizedInitialService);
  const [selectedArea, setSelectedArea] = useState(normalizedInitialArea);
  const [selectedBudget, setSelectedBudget] = useState(normalizedInitialBudget);
  
  // Determine which steps to show initially based on initial values
  const [showSecondStep, setShowSecondStep] = useState(!!initialService);
  const [showThirdStep, setShowThirdStep] = useState(!!initialArea);

  // Update selected values when initial values change
  useEffect(() => {
    console.log('Initial values changed, updating selectedService:', initialService);
    const normalizedService = normalizeOption(initialService);
    if (normalizedService) {
      setSelectedService(normalizedService);
      // Show second step if service is selected
      setShowSecondStep(true);
    }
  }, [initialService]);
  
  useEffect(() => {
    console.log('Initial values changed, updating selectedArea:', initialArea);
    const normalizedArea = normalizeOption(initialArea);
    if (normalizedArea) {
      setSelectedArea(normalizedArea);
      // Show third step if area is selected
      setShowThirdStep(true);
    }
  }, [initialArea]);
  
  useEffect(() => {
    console.log('Initial values changed, updating selectedBudget:', initialBudget);
    const normalizedBudget = normalizeOption(initialBudget);
    if (normalizedBudget) {
      setSelectedBudget(normalizedBudget);
    }
  }, [initialBudget]);

  // Update form step based on available values
  useEffect(() => {
    // Update form step based on what values are available
    if (selectedService && selectedArea && selectedBudget) {
      setFormStep(3);
    } else if (selectedService && selectedArea) {
      setFormStep(2);
    } else if (selectedService) {
      setFormStep(1);
    }
  }, [selectedService, selectedArea, selectedBudget]);

  useEffect(() => {
    // Show form after 2 seconds
    const formTimer = setTimeout(() => {
      setShowForm(true);
    }, 2000);
    
    return () => {
      clearTimeout(formTimer);
    };
  }, []);

  const handleServiceChange = (option) => {
    console.log('Service changed to:', option);
    setSelectedService(option);
    setHasUserInput(true);
    localStorage.setItem('zahoot_hasUserInput', 'true');
  };
  
  const handleAreaChange = (option) => {
    setSelectedArea(option);
    setHasUserInput(true);
    localStorage.setItem('zahoot_hasUserInput', 'true');
  };

  const handleNextStep = () => {
    if (formStep === 1) {
      // Move to step 2 but keep step 1 visible
      setFormStep(2);
      
      // Add a small delay before showing the second dropdown
      setTimeout(() => {
        setShowSecondStep(true);
      }, 300);
    } else if (formStep === 2) {
      // Move to step 3 but keep previous steps visible
      setFormStep(3);
      
      // Add a small delay before showing the third dropdown
      setTimeout(() => {
        setShowThirdStep(true);
      }, 300);
    } else {
      // Handle search - use the global search handler
      handleGlobalSearch();
    }
  };
  
  // Handle search mode changes
  const handleSearchModeChange = (mode) => {
    // Check if trying to switch to AI mode without authentication
    if (mode === 'ai' && !user) {
      // Don't switch to AI mode, show login prompt instead
      setShowLoginPrompt(true);
      setAiError('Please sign in to use AI search');
      return;
    }
    
    setSearchMode(mode);
    
    if (mode === 'quick') {
      setIsFuzzySearchEnabled(true);
      if (toggleFuzzySearch) {
        toggleFuzzySearch(true);
      }
      saveFuzzySearchCriteria(fuzzySearchCriteria, true);
    } else if (mode === 'ai') {
      // For AI mode, disable fuzzy search but don't let it override the mode
      setIsFuzzySearchEnabled(false);
      if (toggleFuzzySearch) {
        toggleFuzzySearch(false);
      }
      saveFuzzySearchCriteria(fuzzySearchCriteria, false);
    } else {
      // Standard mode
      setIsFuzzySearchEnabled(false);
      if (toggleFuzzySearch) {
        toggleFuzzySearch(false);
      }
      saveFuzzySearchCriteria(fuzzySearchCriteria, false);
    }
  };

  // Handle toggle switch for fuzzy search (kept for backward compatibility)
  const handleToggleFuzzySearch = () => {
    const newState = !isFuzzySearchEnabled;
    setIsFuzzySearchEnabled(newState);
    
    // Call parent toggle handler
    if (toggleFuzzySearch) {
      toggleFuzzySearch(newState);
    }
    
    // Also store in search context
    saveFuzzySearchCriteria(fuzzySearchCriteria, newState);
  };
  
  // Handle fuzzy search form submission
  const handleFuzzySearchSubmit = (criteria) => {
    // Call the fuzzy search handler from parent
    if (handleFuzzySearch) {
      handleFuzzySearch(criteria);
    }
    
    // Also store in search context
    saveFuzzySearchCriteria(criteria, true);
    
    // Set user input flag
    setHasUserInput(true);
    localStorage.setItem('zahoot_hasUserInput', 'true');
  };

  // Handle AI search form submission
  const handleAiSearchSubmit = async (e) => {
    e.preventDefault();
    
    if (!aiQuery.trim()) return;
    
    // Check authentication first
    if (!user) {
      setAiError('Please sign in to use AI search');
      return;
    }
    
    // Check if user has credits
    if (userCredits && userCredits.remaining <= 0) {
      setAiError('You\'ve used all your AI search credits');
      showToast('You have no AI search credits remaining. Please upgrade your account to continue using AI search.', 'warning');
      return;
    }
    
    setIsAiLoading(true);
    setAiError(null);
    
    try {
      if (isAiAvailable && !aiError && handleAISearch) {
        // Save AI search state first
        saveAiSearchState(aiQuery, aiQuery);
        
        // Call the AI search handler from parent
        await handleAISearch(aiQuery);
        
        // Set user input flag
        setHasUserInput(true);
        localStorage.setItem('zahoot_hasUserInput', 'true');
        
        // Refresh user credits after search (only here)
        try {
          const creditsData = await getUserAiCredits();
          setUserCredits(creditsData);
        } catch (creditsError) {
          console.error('Error refreshing credits:', creditsError);
        }
      } else {
        setAiError('AI search is not available');
      }
    } catch (error) {
      console.error('AI search error:', error);
      
      if (error.message === 'AUTHENTICATION_REQUIRED') {
        setAiError('Please sign in to use AI search');
      } else if (error.message === 'CREDITS_EXHAUSTED') {
        setAiError('You\'ve used all your AI search credits');
        // Refresh credits to show updated status
        try {
          const creditsData = await getUserAiCredits();
          setUserCredits(creditsData);
        } catch (creditsError) {
          console.error('Error refreshing credits:', creditsError);
        }
      } else {
        setAiError('AI search failed - please try again');
      }
    } finally {
      setIsAiLoading(false);
    }
  };

  // Get the selected values to pass up to parent
  const getFormValues = () => {
    return {
      service: selectedService,
      area: selectedArea,
      budget: selectedBudget
    };
  };

  // Prevent infinite update loops with previous values ref
  const prevValuesRef = useRef({
    service: normalizedInitialService,
    area: normalizedInitialArea,
    budget: normalizedInitialBudget
  });

  // Expose form values to parent component
  useEffect(() => {
    // Update the parent with the current form values only if they actually changed
    const currentValues = getFormValues();
    
    // Check if values actually changed to prevent infinite loops
    const prevValues = prevValuesRef.current;
    const serviceChanged = JSON.stringify(prevValues.service) !== JSON.stringify(currentValues.service);
    const areaChanged = JSON.stringify(prevValues.area) !== JSON.stringify(currentValues.area);
    const budgetChanged = JSON.stringify(prevValues.budget) !== JSON.stringify(currentValues.budget);
    
    if ((serviceChanged || areaChanged || budgetChanged) && window.updateUserFormValues) {
      // Update reference to current values
      prevValuesRef.current = { ...currentValues };
      window.updateUserFormValues(currentValues);
    }
  }, [selectedService, selectedArea, selectedBudget]);

  // Handle login prompt actions
  const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  const handleSignUp = () => {
    setShowLoginPrompt(false);
    // Navigate to signup page
    window.location.href = '/signup?feature=ai-search';
  };

  const handleSignIn = () => {
    setShowLoginPrompt(false);
    // Navigate to login page
    window.location.href = '/signin?redirect=search&feature=ai-search';
  };

  return (
    <div className={`user-page__content 
      ${animationStarted ? 'animate' : ''} 
      ${contentAnimating ? 'animate-up' : ''} 
      ${shiftLeftActive ? 'shift-left' : ''}
      ${isMobileView && shiftLeftActive ? 'shift-mobile' : ''}`}>
      <h1 className="user-page__heading">
        Let's find your<br />
        <span className="highlight">perfect</span> legal partner
      </h1>
      
      {showForm && (
        <div className="user-page__form fade-in">
          {/* Search Form Header with Three-Way Mode Selector */}
          <div className="search-form-header">
            <h2 className="search-form-title">Search UK Solicitors</h2>
            <div className="search-mode-selector">
              <div className="search-mode-tabs">
                <button
                  type="button"
                  className={`search-mode-tab ${searchMode === 'standard' ? 'active' : ''}`}
                  onClick={() => handleSearchModeChange('standard')}
                >
                  Standard
                </button>
                <button
                  type="button"
                  className={`search-mode-tab ${searchMode === 'quick' ? 'active' : ''}`}
                  onClick={() => handleSearchModeChange('quick')}
                >
                  Quick Search
                </button>
                <button
                  type="button"
                  className={`search-mode-tab ${searchMode === 'ai' ? 'active' : ''} ${!user ? 'locked' : ''}`}
                  onClick={() => handleSearchModeChange('ai')}
                  title={!user ? 'Sign in to use AI search' : 'AI-powered natural language search'}
                >
                  {!user ? <FaLock className="tab-icon" /> : <FaRobot className="tab-icon" />}
                  AI Search
                </button>
              </div>
            </div>
          </div>
          
          {/* Render appropriate form based on search mode */}
          {searchMode === 'ai' ? (
            /* AI Search Form */
            <div className="ai-search-form">
              <form onSubmit={handleAiSearchSubmit} className="ai-search-input-container">
                <div className="ai-search-input-wrapper">
                  <div className={`ai-search-icon ${!user ? 'locked' : isAiAvailable && !aiError ? 'active' : 'inactive'}`}>
                    {!user ? (
                      <FaLock />
                    ) : aiError ? (
                      <FaExclamationTriangle />
                    ) : (
                      <FaRobot />
                    )}
                  </div>
                  
                  <input
                    type="text"
                    value={aiQuery}
                    onChange={(e) => setAiQuery(e.target.value)}
                    placeholder={
                      !user ? 'Sign in to use AI search...' :
                      aiError ? 'AI search temporarily unavailable...' : 
                      aiPlaceholder
                    }
                    className="ai-search-input"
                    disabled={!user || !isAiAvailable || !!aiError}
                  />
                  
                  <button
                    type="submit"
                    disabled={!user || isAiLoading || !aiQuery.trim() || !isAiAvailable || !!aiError}
                    className="ai-search-button"
                  >
                    {isAiLoading ? (
                      <div className="ai-search-spinner" />
                    ) : (
                      <FaSearch />
                    )}
                  </button>
                </div>
                
                {/* AI Credits Display */}
                {user && userCredits && (
                  <div className="ai-credits-display">
                    {userCredits.remaining > 0 ? (
                      <span className="credits-remaining">
                        {userCredits.remaining} AI search{userCredits.remaining !== 1 ? 'es' : ''} remaining
                      </span>
                    ) : (
                      <span className="credits-exhausted">
                        AI credits used up
                      </span>
                    )}
                  </div>
                )}
                
                {/* AI Search Status Message */}
                {!user ? (
                  <div className="ai-search-message auth-required">
                    <span className="font-semibold">🎉 Sign up for 3 free AI searches</span> to find the perfect solicitor!
                  </div>
                ) : aiError ? (
                  <div className="ai-search-message error">
                    <span className="font-semibold">Notice:</span> {aiError}. Please try standard search.
                  </div>
                ) : isAiAvailable ? (
                  <div className="ai-search-message info">
                    <span className="font-semibold">Examples:</span> "Divorce solicitors in London", "Property solicitors with 5-star ratings"
                  </div>
                ) : (
                  <div className="ai-search-message warning">
                    <span className="font-semibold">AI search unavailable:</span> Please use standard search
                  </div>
                )}
              </form>
            </div>
          ) : searchMode === 'quick' ? (
            /* Fuzzy Search Form */
            <FuzzySearchForm 
              initialValues={fuzzySearchCriteria}
              practiceAreas={practiceAreas}
              onSearch={handleFuzzySearchSubmit}
            />
          ) : (
            /* Standard Search Form */
            <>
              {/* First step */}
              <ServiceStep 
                serviceOptions={serviceOptions}
                selectedService={selectedService}
                handleServiceChange={handleServiceChange}
                handleNextStep={formStep === 1 ? handleNextStep : null}
              />
              
              {/* Second step */}
              {(formStep >= 2 || showSecondStep) && (
                <AreaStep 
                  areaOptions={areaOptions}
                  selectedArea={selectedArea}
                  handleAreaChange={handleAreaChange}
                  handleNextStep={formStep === 2 ? handleNextStep : null}
                  showSecondStep={showSecondStep}
                />
              )}
              
              {/* Third step */}
              {(formStep >= 3 || showThirdStep) && (
                <BudgetStep 
                  selectedBudget={selectedBudget}
                  setSelectedBudget={setSelectedBudget}
                  dropdownExpanded={dropdownExpanded}
                  setDropdownExpanded={setDropdownExpanded}
                  showThirdStep={showThirdStep}
                  setHasUserInput={setHasUserInput}
                />
              )}
              
              {/* Search button */}
              <SearchButton 
                hasUserInput={hasUserInput} 
                handleGlobalSearch={handleGlobalSearch} 
              />
            </>
          )}
        </div>
      )}
      
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="login-prompt-overlay" onClick={handleCloseLoginPrompt}>
          <div className="login-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <button className="login-prompt-close" onClick={handleCloseLoginPrompt}>
              <FaTimes />
            </button>
            
            <div className="login-prompt-content">
              <div className="login-prompt-icon">
                <FaRobot />
              </div>
              
              <h3 className="login-prompt-title">
                Unlock AI Search
              </h3>
              
              <p className="login-prompt-description">
                Get <strong>3 free AI searches</strong> when you create an account! 
                Use natural language to find the perfect solicitor.
              </p>
              
              <div className="login-prompt-features">
                <div className="feature-item">
                  <FaRobot className="feature-icon" />
                  <span>Smart AI-powered search</span>
                </div>
                <div className="feature-item">
                  <FaSearch className="feature-icon" />
                  <span>Natural language queries</span>
                </div>
                <div className="feature-item">
                  <FaUser className="feature-icon" />
                  <span>3 free searches on signup</span>
                </div>
              </div>
              
              <div className="login-prompt-actions">
                <button className="login-prompt-signup" onClick={handleSignUp}>
                  Sign Up for Free
                </button>
                <button className="login-prompt-signin" onClick={handleSignIn}>
                  Already have an account? Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchForm; 